import React from "react";
import { isServer } from "@base/BuildEnvironment";
import { formatJSX, FormatValues, ReactReturnValue, ReactValue } from "@base/reactUtil/JSXFormat";
import { ReactHelper } from "@base/reactUtil/ReactHelper";
import { CacheUtil } from "@base/util/Cache";
import { Util } from "@base/util/Util";
import { LanguageType } from "@biz/Definitions";
import { LanguageContext } from "@biz/language/LanguageContext";
import { getBundledLanguageFiles, LanguageFile, LanguageId } from "@biz/language/Languages";
import { LocaIconDefault } from "@biz/language/LocaContext.default";

export type LocaFn = (id: LanguageId) => string;

let showLoca = false;
let LocaIcon = LocaIconDefault
let _getLanguageFile: (overrideLanguage?: LanguageType) => LanguageFile;

export function initLoca(showLocaHelper: boolean, locaIcon: typeof LocaIcon, getLanguageFile: (overrideLanguage?: LanguageType) => LanguageFile): void {
  showLoca = showLocaHelper;
  LocaIcon = locaIcon;
  _getLanguageFile = getLanguageFile;
}

export function isShowLoca(): boolean {
  return showLoca;
}

export const LocaContext = {
  connect: (id: string, ...args: string[]): LanguageId => {
    return [id, ...args.filter((a) => !!a).map((a) => Util.capitalizeFirstLetters(a))].join("_") as LanguageId
  },
  LocaIcon: (id: string): React.ReactElement => React.createElement(LocaIcon, { id }),
  replaceIdPlaceholderWithLocaIcon: (str: string | undefined): ReactReturnValue => {
    if (!str)
      return "";
    return showLoca ? formatJSX(str, (id) => React.createElement(LocaIcon, { id, key: id }), { regex: regexLocaIds }) : str
  }
}

export interface ILocaContext {
  languageType: LanguageType;
  _lf: LanguageFile;
  toe: LocaFn;
  t: LocaFn;
  // wrap: (fn: (t: LocaFn) => React.ReactElement) => React.ReactElement;
  f: (id: LanguageId, params?: FormatValues<string | number>) => string;
  c: (str: string) => ReactReturnValue;
  C: (id: LanguageId, params?: FormatValues<ReactValue>) => React.ReactElement;
  CC: (id: LanguageId, params?: FormatValues<ReactValue>) => React.ReactElement;
  H: (id: LanguageId, params?: FormatValues<string>) => React.ReactElement;
  connect: (id: string, str: string) => LanguageId;
}

export const regexLocaIds = /\$\[([^\]]+)]/g;

function getLocaIdText(id: string): string {
  return "$[" + id + "]"
}

function withLocaId(text: string, id: string, showLoca: boolean): string {
  if (text) {
    if (showLoca)
      return text + " " + getLocaIdText(id);
    return text;
  } else {
    return getLocaIdText(id);
  }
}


const _createLocaContext = (_lf: LanguageFile): ILocaContext => {
  const only: LocaFn = (id) => _lf[id] || getLocaIdText(id);
  const onlyOrEmpty: LocaFn = (id) => _lf[id] || ""
  const withLoca: LocaFn = (id) => withLocaId(_lf[id], id, true)

  if (!_lf.languageType)
    throw new Error("LanguageType not set in language!");

  return {
    languageType: _lf.languageType as LanguageType,
    _lf,
    toe: showLoca ? withLoca : onlyOrEmpty,
    t: showLoca ? withLoca : only,
    // wrap: (fn: (t: LocaFn) => React.ReactElement) => {
    //   const ids: string[] = [];
    //   const t2: LocaFn = (id) => {
    //     ids.push(id);
    //     return _lf[id]
    //   }
    //   const el = fn(t2);
    //   return React.createElement(React.Fragment, null,
    //     el,
    //     showLoca && ids.map((id, index) => React.createElement(LocaIcon, { id, key: index }))
    //   )
    // },
    c: LocaContext.replaceIdPlaceholderWithLocaIcon,
    f: (id, params) => withLocaId(params !== undefined ? formatJSX(_lf[id], params) as string : _lf[id], id, showLoca),
    C: (id, params) => React.createElement(React.Fragment, null,
      params !== undefined ? formatJSX(_lf[id], params) : onlyOrEmpty(id),
      showLoca && React.createElement(LocaIcon, { id })
    ),
    CC: (id, params) => React.createElement(React.Fragment, null,
      params !== undefined ? formatJSX(_lf[id], params) || only(id) : only(id),
      showLoca && React.createElement(LocaIcon, { id })
    ),
    H: (id, params) => React.createElement(React.Fragment, null,
      ReactHelper.createHtmlSpan(params !== undefined ? formatJSX(_lf[id], params) : _lf[id]),
      showLoca && React.createElement(LocaIcon, { id })
    ),
    connect: LocaContext.connect
  }
}

export const calcLocaContext = CacheUtil.memo(_createLocaContext, "getLocaContext", {
  idsFromArgs: 1,
  inputsEqualFn: "ref",
  resultEqualFn: "ref",
});

export function useLocaContext(): ILocaContext {
  const l = React.useContext(LanguageContext);
  const ret = calcLocaContext(l);
  return ret;
}

export function _getLocaContext(language?: LanguageType | LanguageFile): ILocaContext {
  if (typeof language === "object") {
    return calcLocaContext(language);
  } else {
    const lf = _getLanguageFile(language);
    const ret = calcLocaContext(lf);

    if (isServer()) {
      console.warn("_getLocaContext is used on server!: ", new Error().stack)
    }
    return ret;
  }
}

export function getGermanLocaContext(): ILocaContext {
  const de_at = _getLanguageFile("de_AT") || getBundledLanguageFiles().de_AT;
  return _getLocaContext(de_at);
}

